import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  leagueKey,
  leagueLabel,
  useLeagueFilterData,
} from "@/components/LichThiDauByLeague/useLeagueFilterData";
import useFixedPortalMenu, { PORTAL_SHELL_STYLE } from "@/hooks/useFixedPortalMenu";
import { getPortalThemeProps } from "@/constants/themes";

function SearchGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="8.5" cy="8.5" r="5.25" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12.4 12.4L16.2 16.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function CloseGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M4.2 4.2l7.6 7.6M11.8 4.2l-7.6 7.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3.2 8.2l3.1 3.2 6.5-6.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M4 6l4 4 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LeagueLogo({ src, label }) {
  const [broken, setBroken] = useState(false);
  const show = Boolean(src) && !broken;
  const letter = String(label || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <span className={`kbv-ltd-sb-logo ${show ? "" : "is-fallback"}`}>
      {show ? <img src={src} alt="" onError={() => setBroken(true)} /> : <span>{letter}</span>}
    </span>
  );
}

export default function LeagueSelect({
  selectedSlug,
  currentLeague,
  onSelect,
  themeStyle,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const {
    loadingHot,
    keyword,
    setKeyword,
    searching,
    dropdownLeagues,
    isSelected,
    selectLeague,
    debouncedKeyword,
  } = useLeagueFilterData({
    selectedSlug,
    currentLeague,
    onSelect,
    searchOpen: open,
  });

  const close = () => setOpen(false);
  const { menuStyle, updatePosition } = useFixedPortalMenu({
    open,
    onClose: close,
    triggerRef,
    menuRef,
  });

  const label = leagueLabel(currentLeague) || (loadingHot ? "Đang tải..." : "Chọn giải đấu");
  const logo = currentLeague?.logo;

  const { theme: portalTheme, className: portalClassName } =
    getPortalThemeProps(rootRef.current);

  const handleSelect = async (league) => {
    const ok = await selectLeague(league);
    if (ok) setOpen(false);
  };

  return (
    <div ref={rootRef} className="kbv-ltd-sb-select-wrap">
      <button
        ref={triggerRef}
        type="button"
        className={`kbv-ltd-sb-select ${open ? "is-open" : ""}`}
        aria-label="Chọn giải đấu"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => {
          setOpen((prev) => {
            if (prev) return false;
            updatePosition();
            return true;
          });
        }}
      >
        <LeagueLogo src={logo} label={label} />
        <span className="kbv-ltd-sb-select-label" title={label}>
          {label}
        </span>
        <span className={`kbv-ltd-sb-chevron ${open ? "is-open" : ""}`}>
          <ChevronGlyph />
        </span>
      </button>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={portalClassName}
            data-theme={portalTheme}
            style={{ ...themeStyle, ...PORTAL_SHELL_STYLE }}
          >
            <div
              ref={menuRef}
              style={{ ...themeStyle, ...menuStyle }}
              className="kbv-ltd-sb-drop"
              role="listbox"
              aria-label="Danh sách giải đấu"
            >
              <div className="kbv-ltd-sb-drop-head">
                <div className="kbv-ltd-sb-drop-search">
                  <SearchGlyph />
                  <input
                    type="text"
                    autoFocus
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Tìm tên giải đấu..."
                  />
                </div>
                <button
                  type="button"
                  className="kbv-ltd-sb-drop-close"
                  aria-label="Đóng"
                  onClick={close}
                >
                  <CloseGlyph />
                </button>
              </div>
              <div className="kbv-ltd-sb-drop-caption">
                {debouncedKeyword ? "Kết quả tìm kiếm" : "Giải đấu hot"}
              </div>
              <div className="kbv-ltd-sb-drop-list">
                {searching ? (
                  <div className="kbv-ltd-sb-drop-empty">Đang tìm...</div>
                ) : dropdownLeagues.length === 0 ? (
                  <div className="kbv-ltd-sb-drop-empty">Không tìm thấy giải đấu</div>
                ) : (
                  dropdownLeagues.map((league) => {
                    const selected = isSelected(league);
                    const name = leagueLabel(league);
                    return (
                      <button
                        key={leagueKey(league)}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => handleSelect(league)}
                        className={`kbv-ltd-sb-drop-item ${selected ? "is-active" : ""}`}
                      >
                        <LeagueLogo src={league.logo} label={name} />
                        <span className="kbv-ltd-sb-drop-name">{name}</span>
                        {selected ? (
                          <span className="kbv-ltd-sb-drop-check">
                            <CheckGlyph />
                          </span>
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
